/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, X, ChevronLeft, Upload, FileText } from 'lucide-react';
import { CheckPicker } from 'rsuite';
import MultiPOGeneralDetails from '@/Components/Modals/MultiPoModal/MultiPOGeneralDetails';
import processFlowServices from '@/Services/Analytics Services/process-flow.services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { notificationServices } from '@/Services';
import { getHigherUnitName, getUnitInLowerUnit, convertWeightUnit, imageToBase64 } from '@/Helpers';
import { formActions, modalActions } from '@/Actions';
import { orderTypes } from '@/Constants';
import { AlertModal } from '@/Components/Modals';
import PRDocViewer from './PRDocViewer';
import moment from 'moment';


// PR line items are consumed by Vendor POs, so the picker must offer exactly what a Vendor PO
// accepts. Read off the same orderTypes entry CreatePurchaseOrder filters on, so restricting one
// restricts the other and the two lists cannot drift apart.
const VENDOR_ORDER_SKU_TYPES = orderTypes?.find((type) => {
    return type?.enum === 'VENDOR';
})?.skuTypes || [];

const columns = [
    {
        title: 'S.No',
        key: 'sno',
        width: 60,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Product Name',
        key: 'productName',
        width: 250,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'SKU Code',
        key: 'skuCode',
        width: 100,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Qty',
        key: 'qty',
        width: 120,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Pending GRN Qty',
        key: 'pendingGrnQty',
        width: 130,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Open PR Qty',
        key: 'openPrQty',
        width: 130,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Pending PR Approval Qty',
        key: 'pendingPrApprovalQty',
        width: 140,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Remarks',
        key: 'remarks',
        width: 220,
        noPadding: true,
        customCol: true,
    },
    {
        title: 'Actions',
        key: 'actions',
        width: 80,
        noPadding: true,
        customCol: true,
    },
];

const REMARKS_MAX_LENGTH = 100;

function PurchaseRequestCreate({ refetch, editMode = false, viewMode = false, initialData = {} }) {
    const history = useHistory();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        selectedStoreId: '',
        selectedProcessId: '',
        prName: 'FG Items Apr 2026',
        selectedProducts: [],
        prDate: moment().format('YYYY-MM-DD'),
        requireByDate: null,
        uploadedDoc: '',
        uploadedDocFileName: '',
    });

    const [tableSearch, setTableSearch] = useState('');
    const [skuStockInfo, setSkuStockInfo] = useState([]);
    const [showDocViewer, setShowDocViewer] = useState(false);

    useEffect(() => {
        if ((editMode || viewMode) && initialData && Object.keys(initialData).length > 0) {
            setFormData(prev => ({
                ...initialData,
                selectedProducts: initialData.selectedProducts || [],
            }));
        }
    }, [initialData, editMode, viewMode]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);
    const productList = useSelector((state) => state.form?.ALL_PRODUCTS?.data?.LIST || []);
    // Loaded by PurchaseRequestList; used to resolve the PR creator's name for view/edit.
    const usersList = useSelector((state) => state.wastage?.usersList || []);
    const raisedByName = useMemo(() => {
        if (!formData?.requestedBy) return '';
        return usersList.find((u) => u.id === formData?.requestedBy)?.displayName || '-';
    }, [usersList, formData?.requestedBy]);

    useEffect(() => {
        if (storeList?.length === 0) {
            dispatch(formActions.gateway({
                entity: 'FILTERED_STORES',
                req: {
                    params: {
                        id: '',
                    },
                },
                method: 'LIST',
            }));
        }
    }, []);

    const storeOptions = useMemo(() =>
        storeList.map(store => ({ label: store.name, value: store.id?.toString() })),
        [storeList]);

    const { data: processNodes, isLoading: isLoadingProcesses } = useQuery({
        queryKey: ['processNodes', formData.selectedStoreId],
        queryFn: () => processFlowServices.fetchNodes({ params: { storeId: formData.selectedStoreId } }),
        enabled: !!formData.selectedStoreId,
    });

    const processOptions = useMemo(() => {
        const nodes = processNodes?.data?.nodes || [];
        return nodes.map(node => ({ label: `${node.data?.heading} - ${node.data?.sectionName}`, value: node.id?.toString() }));
    }, [processNodes]);

    const { data: pendingGrnData, isLoading: isLoadingPendingGrn } = useQuery({
        queryKey: ['pendingGrn', formData.selectedStoreId],
        queryFn: () => purchaseRequestServices.fetchPendingGrn({
            data: {
                storeId: Number(formData.selectedStoreId),
                subType: 'VENDOR',
            },
        }),
        enabled: !!formData.selectedStoreId,
    });

    // entityId in the pending-grn response is the variantId — build
    // variantId → item maps for the three qty columns.
    const pendingGrnMaps = useMemo(() => {
        const d = pendingGrnData?.data || {};
        const toMap = (list) => {
            const map = {};
            (list || []).forEach((item) => {
                if (item?.entityId != null) map[item.entityId] = item;
            });
            return map;
        };
        return {
            pendingGrn: toMap(d?.posPendingGrn),
            openPr: toMap(d?.approvedPrsNotUsedInPo),
            pendingApproval: toMap(d?.unapprovedPrs),
        };
    }, [pendingGrnData]);

    // Variants already on this PR stay pickable even if they would not pass the filter today, so
    // opening a PR raised before the restriction does not strip its items out of the picker.
    const existingVariantIds = useMemo(() => {
        return new Set((initialData?.selectedProducts || []).map((p) => p?.variantId));
    }, [initialData]);

    const productOptions = useMemo(() => {
        const data = [];
        productList?.forEach((product) => {
            // Traded products are finished goods the business buys in rather than produces, so a
            // Vendor PO takes them regardless of skuType — same exception CreatePurchaseOrder makes.
            const isVendorOrderable = product?.isEnabled !== false
                && (product?.traded || VENDOR_ORDER_SKU_TYPES?.includes(product?.skuType));
            product?.variants?.forEach((variant) => {
                if (!isVendorOrderable && !existingVariantIds?.has(variant?.id)) {
                    return;
                }
                data.push({
                    label: `${product?.productName}(${variant?.displayName})${variant?.clientVariantId ? ` - ${variant?.clientVariantId}` : ''}`,
                    value: variant?.id,
                    variantId: variant?.id,
                    productId: product?.id,
                    productName: product?.productName,
                    displayName: variant?.displayName,
                    skuCode: variant?.clientVariantId || product?.skuCode,
                    unit: getHigherUnitName(product?.unit),
                    skuType: product?.skuType,
                });
            });
        });
        return data;
    }, [productList, existingVariantIds]);

    const { mutate: createPR, isLoading: isSubmitting } = useMutation({
        mutationFn: purchaseRequestServices.createPurchaseRequest,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Purchase Request Created Successfully',
                });
                dispatch(modalActions.toggleCustomModal(false));
                refetch?.();
            } else {
                notificationServices.generateNotification({
                    type: 'error',
                    message: res.message || 'Something went wrong',
                });
            }
        },
        onError: (err) => {
            notificationServices.generateNotification({
                type: 'error',
                message: err.message || 'Error creating PR',
            });
        }
    });

    const handleAddProduct = (selectedIds) => {
        const newSelectedProducts = selectedIds.map(id => {
            const existing = formData.selectedProducts.find(p => p.variantId === id);
            if (existing) return existing;

            const option = productOptions.find(opt => opt.value === id);
            return {
                ...option,
                id: option.variantId,
                qty: '',
            };
        });
        updateFormData('selectedProducts', newSelectedProducts);
    };

    const handleRemoveProduct = (productId) => {
        updateFormData('selectedProducts', formData.selectedProducts.filter((p) => p.id !== productId));
    };

    const handleQtyChange = (productId, newQty) => {
        updateFormData('selectedProducts',
            formData.selectedProducts.map((p) =>
                p.id === productId ? { ...p, qty: newQty } : p
            )
        );
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const base64Url = await imageToBase64(file);
            setFormData(prev => ({
                ...prev,
                uploadedDoc: base64Url,
                uploadedDocFileName: file.name,
            }));
        }
        e.target.value = '';
    };

    const handleRemoveDoc = () => {
        setFormData(prev => ({ ...prev, uploadedDoc: '', uploadedDocFileName: '' }));
    };

    const handleRemarksChange = (productId, newRemarks) => {
        updateFormData('selectedProducts',
            formData.selectedProducts.map((p) =>
                p.id === productId
                    ? { ...p, remarks: newRemarks.slice(0, REMARKS_MAX_LENGTH) }
                    : p
            )
        );
    };


    const fetchSkuStock = async (storeId) => {
        if (!storeId) {
            setSkuStockInfo([]);
            return;
        }
        try {
            const response = await inventoryAnalyticsServices.skuStock({
                params: {
                    type: 'stock',
                    storeId: storeId,
                },
            });
            const variantCurrentStockQtyMap = {};
            Object.entries(response?.data || {})?.forEach(([, value]) => {
                value?.currentStock?.forEach((ele) => {
                    variantCurrentStockQtyMap[ele?.variantId] = ele?.rawQty;
                });
            });
            setSkuStockInfo([
                {
                    name: 'Available',
                    bgColor: '#abc3',
                    color: 'black',
                    values: variantCurrentStockQtyMap,
                },
            ]);
        } catch (error) {
            console.error('Error fetching sku stock info', error);
        }
    };

    const renderQtyInfo = (variantId, unit) => {
        if (!skuStockInfo?.length) return null;
        const info = skuStockInfo[0];
        const val = info.values[variantId] ?? 0;
        if (val === undefined || val === null) return null;

        return (
            <div className='flex gap-2 mt-1'>
                <span
                    className='text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center justify-center whitespace-nowrap w-max'
                    style={{ backgroundColor: info.bgColor, color: info.color }}
                >
                    <span className='mr-1 opacity-80'>{info.name}:</span>
                    <span>
                        {convertWeightUnit(val, unit, undefined, true)}
                    </span>
                </span>
            </div>
        );
    };

    // Renders one of the pending-qty cells (Pending GRN / Open PR / Pending PR
    // Approval). Qtys come in the lower unit, so convert via the product's unit;
    // hovering shows the related PO / PR ids.
    const renderPendingQtyCell = (map, row, qtyKey, infoKey, idKey) => {
        const item = map?.[row?.variantId];
        const raw = item?.[qtyKey];
        if (raw === undefined || raw === null) {
            return <span className='text-sm text-gray-400 px-1'>-</span>;
        }
        const productUnit = productList?.find((p) => p.id === row?.productId)?.unit;
        const refIds = (item?.[infoKey] || [])
            .map((info) => info?.[idKey])
            .filter(Boolean)
            .join(', ');
        return (
            <span className='text-sm font-medium text-gray-700 px-1' title={refIds}>
                {convertWeightUnit(raw, productUnit, undefined, true)}
            </span>
        );
    };

    const customRowData = {
        sno: (row, index) => <p className='px-1'>{index + 1}</p>,
        productName: (row) => (
            <div className='flex flex-col'>
                <span className='font-medium text-gray-800'>{`${row.productName}(${row.displayName})`}</span>
                {renderQtyInfo(row.variantId, productList?.find(p => p.id === row.productId)?.unit)}
            </div>
        ),
        skuCode: (row) => <span className='bg-gray-100 px-2 py-1 rounded text-xs font-mono'>{row.skuCode || 'N/A'}</span>,
        qty: (row) => {
            const isCountOrPcs = ['count', 'pcs'].includes(row.unit?.toLowerCase());
            const isDecimal = row.qty && Number(row.qty) % 1 !== 0;
            const hasError = isCountOrPcs && isDecimal;

            return (
                <div className='flex flex-col items-start'>
                    <div className='flex items-center gap-1.5'>
                        <Input
                            type='number'
                            min={0}
                            disabled={viewMode}
                            onKeyDown={(e) => {
                                if (e.key === '-') {
                                    e.preventDefault();
                                }
                            }}
                            value={row.qty}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || Number(val) >= 0) {
                                    handleQtyChange(row.id, val);
                                }
                            }}
                            className={`w-20 text-start h-8 text-sm ${hasError ? 'border-red-500 ring-red-500' : ''}`}
                        />
                        <span className='text-xs text-gray-600 whitespace-nowrap'>{row.unit || 'Nos'}</span>
                    </div>
                    {hasError ? (
                        <span className='text-[9px] text-red-500 mt-1 leading-tight text-center font-bold'>
                            Can't enter in decimal
                        </span>
                    ) : null}
                </div>
            );
        },
        pendingGrnQty: (row) => renderPendingQtyCell(pendingGrnMaps.pendingGrn, row, 'pendingForGrnQty', 'poInfo', 'clientPoId'),
        openPrQty: (row) => renderPendingQtyCell(pendingGrnMaps.openPr, row, 'prAvailableQty', 'prInfo', 'generatedPrId'),
        pendingPrApprovalQty: (row) => renderPendingQtyCell(pendingGrnMaps.pendingApproval, row, 'qty', 'prInfo', 'generatedPrId'),
        remarks: (row) => (
            <div className='flex flex-col items-start'>
                <Input
                    type='text'
                    maxLength={REMARKS_MAX_LENGTH}
                    disabled={viewMode}
                    value={row.remarks || ''}
                    onChange={(e) => handleRemarksChange(row.id, e.target.value)}
                    placeholder='Optional remarks'
                    className='w-52 text-start h-8 text-sm'
                />
                <span className='text-[9px] text-gray-400 mt-1'>
                    {(row.remarks || '').length}/{REMARKS_MAX_LENGTH}
                </span>
            </div>
        ),
        actions: (row) => (
            <Button
                variant='ghost'
                size='icon'
                disabled={viewMode}
                onClick={() => handleRemoveProduct(row.id)}
                className={`text-gray-400 hover:text-red-500 h-8 w-8 ${viewMode ? 'hidden' : ''}`}
            >
                <X size={16} />
            </Button>
        ),
    };

    const handlePRSubmit = () => {
        if (!formData.selectedStoreId) {
            notificationServices.generateNotification({ type: 'error', message: 'Please select a store' });
            return;
        }
        if (!formData.requireByDate) {
            notificationServices.generateNotification({ type: 'error', message: 'Please select Require By Date' });
            return;
        }
        if (formData.selectedProducts.length === 0) {
            notificationServices.generateNotification({ type: 'error', message: 'Please add at least one product' });
            return;
        }
        const productsMissingQty = formData.selectedProducts?.filter(p => !p.qty);
        if (productsMissingQty?.length > 0) {
            const missingNames = productsMissingQty.map(p => `${p.productName}(${p.displayName || ''})`).join(', ');
            notificationServices.generateNotification({ type: 'error', message: `Please enter quantity for: ${missingNames}` });
            return;
        }
        // Decimal validation check
        const hasDecimalError = formData.selectedProducts.some(p => {
            const isCountOrPcs = ['count', 'pcs'].includes(p.unit?.toLowerCase());
            const isDecimal = p.qty && Number(p.qty) % 1 !== 0;
            return isCountOrPcs && isDecimal;
        });

        if (hasDecimalError) {
            notificationServices.generateNotification({
                type: 'error',
                message: 'Some products with unit Count/Pcs have decimal quantities. Please correct them.'
            });
            return;
        }

        dispatch(modalActions.toggleAlert(true, {
            heading: 'Confirm Submission',
            message: 'Are you sure you want to submit this Purchase Request?',
            confirmText: 'Yes, Submit',
            cancelText: 'Cancel',
            handleConfirm: () => {
                dispatch(modalActions.toggleAlert(false));

                const payload = {
                    data: {
                        type: "PURCHASE_ORDER",
                        ...(formData?.prId ? { id: formData?.prId } : {}),
                        storeId: Number(formData.selectedStoreId) || null,
                        processId: Number(formData.selectedProcessId) || null,
                        jsonData: {
                            description: formData.prName,
                            requireByDate: formData.requireByDate
                                ? moment(formData.requireByDate).format('YYYY-MM-DD')
                                : null,
                            uploadedDoc: formData.uploadedDoc || '',
                            uploadedDocFileName: formData.uploadedDocFileName || '',
                        },
                        items: formData.selectedProducts.map(p => {
                            const remarks = p.remarks?.trim()
                                ? p.remarks.trim().slice(0, REMARKS_MAX_LENGTH)
                                : '';
                            return {
                                entityId: Number(p.variantId),
                                qty: getUnitInLowerUnit(p?.unit, parseFloat(p.qty)),
                                // Persisted in the item's jsonData blob (round-tripped by
                                // get_pr_by_id); backend needs no change.
                                jsonData: { remarks },
                            };
                        }),
                    }
                };
                createPR(payload);
            }
        }));
    };
    useEffect(() => {
        const handleExternalSubmit = () => handlePRSubmit();
        window.addEventListener('SUBMIT_PURCHASE_REQUEST', handleExternalSubmit);
        return () => window.removeEventListener('SUBMIT_PURCHASE_REQUEST', handleExternalSubmit);
    }, [formData, handlePRSubmit]);

    const filteredTableData = useMemo(() => {
        const products = formData?.selectedProducts || [];
        if (!tableSearch) return products;
        return products.filter(p =>
            p.productName?.toLowerCase().includes(tableSearch.toLowerCase()) ||
            p.skuCode?.toLowerCase().includes(tableSearch.toLowerCase())
        );
    }, [formData?.selectedProducts, tableSearch]);

    const handleCustomCol = useMemo(() => (column) => {
        return (
            <div className='w-full flex justify-start text-[11px] font-semibold text-gray-600 uppercase tracking-wider'>
                {column.title}
            </div>
        );
    }, []);

    const filteredColumns = useMemo(() => {
        if (viewMode) return columns.filter(col => col.key !== 'actions');
        return columns;
    }, [viewMode]);

    return (
        <div className='flex flex-col bg-white'>
            <div className='flex-1 overflow-auto p-2 pt-0'>
                <AlertModal />
                <PRDocViewer
                    open={showDocViewer}
                    onClose={() => setShowDocViewer(false)}
                    docUrl={formData.uploadedDoc}
                    fileName={formData.uploadedDocFileName}
                />
                <MultiPOGeneralDetails
                    viewMode={viewMode}
                    externalState={{ ...formData, raisedByName }}
                    fields={[
                        {
                            id: 'selectedStoreId',
                            type: 'select',
                            label: 'Procuring Store',
                            required: true,
                            dataKey: 'storeList',
                            placeholder: 'Select Procuring Store',
                        },
                        {
                            id: 'selectedProcessId',
                            type: 'select',
                            label: 'Procuring location',
                            dataKey: 'processList',
                            placeholder: 'Select Procuring location',
                            enableField: ['selectedStoreId'],
                            disabled: isLoadingProcesses,
                        },
                        {
                            id: 'prName',
                            type: 'text',
                            label: 'Purchase Request Name',
                            placeholder: 'Enter Purchase Request Name',
                        },
                        {
                            id: 'prDate',
                            type: 'display',
                            label: 'Purchase Request Date',
                        },
                        {
                            id: 'requireByDate',
                            type: 'date',
                            label: 'Require By Date',
                            required: true,
                            placeholder: 'Select Require By Date',
                            shouldDisableDate: true,
                        },
                        // Raised By is only meaningful for an existing PR (view/edit).
                        ...((viewMode || editMode)
                            ? [{
                                id: 'raisedByName',
                                type: 'display',
                                label: 'Raised By',
                            }]
                            : []),
                    ]}
                    lists={{
                        storeList: storeOptions,
                        processList: processOptions,
                    }}
                    onFieldChange={(fieldId, value) => {
                        updateFormData(fieldId, value);
                        if (fieldId === 'selectedStoreId') {
                            fetchSkuStock(value);
                        }
                    }}
                />

                {!viewMode && (
                    <div className='mb-3 flex items-center justify-between'>
                        <div className='flex items-center gap-3'>
                            <span className='text-xs font-bold text-gray-700 whitespace-nowrap'>Add Products:</span>
                            <CheckPicker
                                data={productOptions}
                                value={(formData?.selectedProducts || []).map(p => p.variantId)}
                                onChange={handleAddProduct}
                                placeholder='Select Products...'
                                style={{ width: '350px' }}
                                menuStyle={{ zIndex: 999999 }}
                                cleanable={false}
                                size='md'
                                virtualized
                                disabled={!formData.selectedStoreId}
                            />
                            <input
                                type='file'
                                id='pr-doc-upload'
                                className='hidden'
                                onChange={handleFileUpload}
                                accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg'
                            />
                            <label
                                htmlFor='pr-doc-upload'
                                className='flex items-center gap-2 px-3 h-9 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors text-sm text-gray-600'
                            >
                                <Upload size={16} />
                                <span className='max-w-[180px] truncate'>
                                    {formData.uploadedDocFileName || 'Upload Doc'}
                                </span>
                            </label>
                            {formData.uploadedDoc && (
                                <>
                                    <button
                                        type='button'
                                        onClick={() => setShowDocViewer(true)}
                                        className='text-blue-500 hover:text-blue-700 transition-colors'
                                        title='View Doc'
                                    >
                                        <FileText size={16} />
                                    </button>
                                    <button
                                        type='button'
                                        onClick={handleRemoveDoc}
                                        className='text-gray-400 hover:text-red-500 transition-colors'
                                        title='Remove Doc'
                                    >
                                        <X size={16} />
                                    </button>
                                </>
                            )}
                        </div>

                        <div className='flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-gray-300 focus-within:border-blue-400'>
                            <Search size={14} className='text-gray-400' />
                            <input
                                type='text'
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                placeholder='Search ...'
                                className='bg-transparent border-none outline-none text-sm w-48 h-5'
                            />
                        </div>
                    </div>
                )}

                {viewMode && formData.uploadedDoc && (
                    <div className='mb-3 flex items-center gap-2'>
                        <span className='text-xs font-bold text-gray-700 whitespace-nowrap'>Attached Doc:</span>
                        <button
                            type='button'
                            onClick={() => setShowDocViewer(true)}
                            className='flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm underline'
                        >
                            <FileText size={14} />
                            <span className='max-w-[250px] truncate'>
                                {formData.uploadedDocFileName || 'View Document'}
                            </span>
                        </button>
                    </div>
                )}

                <div className='border border-gray-200 rounded-lg overflow-hidden'>
                    <div className='h-[calc(100vh-250px)]'>
                        <VariantTable
                            data={filteredTableData}
                            columns={filteredColumns}
                            customRowData={customRowData}
                            customCol={handleCustomCol}
                            customInfoLine='No products added yet.'
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PurchaseRequestCreate;
